import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  AlertTriangle, Banknote,
  CheckCircle2,
  CreditCard,
  Fuel,
  Loader2,
  type LucideIcon,
  Percent,
  Receipt,
  ShieldAlert,
  Store,
  TrendingUp,
  UserPlus,
  UserX,
  Wallet,
  XCircle,
} from "lucide-react";
import { AdminRole, DashboardPeriod, InvoiceStatus } from "@cpg/common";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StatCard } from "@/components/stat-card";
import { PeriodSwitcher } from "@/components/period-switcher";
import { formatUsdAmount } from "@/components/amount";
import { hasRole } from "@/lib/roles";
import { cn, formatStatusLabel } from "@/lib/utils";
import { INVOICE_STATUS_COLOR, type StatusColor } from "@/lib/status-colors";
import { getFinanceDashboard, getHealthDashboard, getMerchantsDashboard, getPaymentsDashboard, } from "@/api/dashboard";
import { type DashboardCountTile, type StaleRateItem } from "@/api/types";
import { StatusBadge } from "@/components/status-badge";

// Real implementation of the first Phase 4 page (Task 24): four
// independently role-gated overview blocks (payments, finance, health,
// merchants), each fetching its own backoffice-api `/v1/dashboard/*`
// endpoint (Task 16). Replaces the Task 22 placeholder. `period` is
// page-local state (no URL persistence — a period switch here doesn't need
// to survive a reload or be linkable, unlike the paginated list pages later
// in this feature).
export const Route = createFileRoute("/_app/dashboard")({
  component: DashboardPage,
});

// ─── shared block chrome ─────────────────────────────────────────────────────
//
// Icon badge in the block header echoes the sidebar logo / avatar chip's own
// `h-9 w-9 rounded-lg` treatment (`_app.tsx`) — the same "square icon token"
// used consistently across the shell. `SectionLabel` mirrors the sidebar's
// nav-section caption style so every eyebrow label on this page (stat
// tiles, ranked-list titles, table headers) reads as one system.

function BlockCard({
                     title,
                     description,
                     icon: Icon,
                     children,
                   }: {
  title: string;
  description: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card className="shadow-none">
      <CardHeader className="flex flex-row items-center gap-3 space-y-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="h-[18px] w-[18px]"/>
        </div>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function BlockStatus({ label, icon: Icon, spin }: { label: string; icon?: LucideIcon; spin?: boolean }) {
  return (
    <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
      {Icon && <Icon className={cn("h-4 w-4 shrink-0", spin && "animate-spin")}/>}
      {label}
    </div>
  );
}

const TABLE_HEAD_ROW = "bg-muted text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground";

// ─── payments block (INVOICES_VIEWER / SUPERADMIN) ──────────────────────────

function PaymentsBlock({ roles, period }: { roles: AdminRole[]; period: DashboardPeriod }) {
  const canView = hasRole(roles, AdminRole.INVOICES_VIEWER);

  const { data, isPending, isError } = useQuery({
    queryKey: ["dashboard", "payments", period],
    queryFn: () => getPaymentsDashboard(period),
    enabled: canView,
  });

  if (!canView) return null;

  return (
    <BlockCard
      title="Payments"
      description="Invoice volume and completion, across all merchants."
      icon={CreditCard}
    >
      {isPending ? (
        <BlockStatus icon={Loader2} spin label="Loading payments…"/>
      ) : isError || !data ? (
        <BlockStatus icon={AlertCircle} label="Failed to load payments data."/>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label="Volume" value={formatUsdAmount(data.volumeUsd)} icon={Wallet} tone="info"/>
            <StatCard
              label="Completion rate"
              value={`${Math.round(data.completionRate * 100)}%`}
              icon={CheckCircle2}
              tone="success"
            />
            <StatCard
              label="Average completed"
              value={formatUsdAmount(data.averageCompletedUsd)}
              icon={Receipt}
              tone="neutral"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.values(InvoiceStatus).map((status) => (
              <StatusBadge
                key={status}
                label={`${status}: ${data.invoiceCountsByStatus[status]}`}
                color={INVOICE_STATUS_COLOR[status]}
              />
            ))}
          </div>
        </>
      )}
    </BlockCard>
  );
}

// ─── finance block (FINANCE_VIEWER) ─────────────────────────────────────────
//
// `perAsset` is a plain table, not the full DataTable — at most a handful of
// assets, unpaginated and unsorted, per the task brief.

function FinanceBlock({ roles, period }: { roles: AdminRole[]; period: DashboardPeriod }) {
  const canView = hasRole(roles, AdminRole.FINANCE_VIEWER);

  const { data, isPending, isError } = useQuery({
    queryKey: ["dashboard", "finance", period],
    queryFn: () => getFinanceDashboard(period),
    enabled: canView,
  });

  if (!canView) return null;

  return (
    <BlockCard
      title="Finance"
      description="Fees, gas spend, and net revenue, across all assets."
      icon={Banknote}
    >
      {isPending ? (
        <BlockStatus icon={Loader2} spin label="Loading finance data…"/>
      ) : isError || !data ? (
        <BlockStatus icon={AlertCircle} label="Failed to load finance data."/>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label="Fees" value={formatUsdAmount(data.feesUsd)} icon={Percent} tone="info"/>
            <StatCard
              label="Gas spent"
              value={formatUsdAmount(data.gasSpentUsd)}
              icon={Fuel}
              tone="warning"
            />
            <StatCard label="Net" value={formatUsdAmount(data.netUsd)} icon={TrendingUp} tone="success"/>
          </div>
          {data.perAsset.length === 0 ? (
            <BlockStatus label="No asset activity in this period."/>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                <tr className={TABLE_HEAD_ROW}>
                  <th className="px-4 py-2.5 font-semibold">Asset</th>
                  <th className="px-4 py-2.5 font-semibold">Network</th>
                  <th className="px-4 py-2.5 font-semibold">Fees</th>
                  <th className="px-4 py-2.5 font-semibold">Gas spent</th>
                  <th className="px-4 py-2.5 font-semibold">Net</th>
                </tr>
                </thead>
                <tbody>
                {data.perAsset.map((row) => (
                  <tr key={row.assetId} className="border-t">
                    <td className="px-4 py-2 font-medium">{row.symbol}</td>
                    <td className="px-4 py-2">
                      <StatusBadge label={formatStatusLabel(row.network)} color="neutral" />
                    </td>
                    <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatUsdAmount(row.feesUsd)}
                    </td>
                    <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatUsdAmount(row.gasSpentUsd)}
                    </td>
                    <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatUsdAmount(row.netUsd)}
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </BlockCard>
  );
}

// ─── health block (no role required) ────────────────────────────────────────
//
// No period param, never re-fetches on period change. Every one of the five
// possible keys is independently present/absent already from the backend
// (`DashboardHealthService` gates each per-caller-role); this block simply
// renders whichever keys happen to be present, no frontend role logic of its
// own — same "just render whichever keys are present" convention Task 23's
// search response already established. Transient pool availability is
// dropped from this page per product ask — the backend field is left
// untouched (still gated per-role), only its render here is removed.

function HealthCountStatCard({
                               label,
                               tile,
                               icon,
                               tone,
                             }: {
  label: string;
  tile: DashboardCountTile;
  icon: LucideIcon;
  tone: StatusColor;
}) {
  const query = tile.filter.query;

  // `StatCard`'s own `link` prop only carries a plain `{ to, label }` pair
  // (no query-string support) — for the two count tiles whose deep link
  // needs a `search` param, a raw `<Link>` is composed here as the `subLine`
  // instead of going through `StatCard`'s `link` slot (kept visually
  // consistent with `StatCard`'s own internal link styling). See task-24
  // report for this judgment call.
  if (!query) {
    return (
      <StatCard
        label={label}
        value={tile.count}
        link={{ to: tile.filter.path, label: "View" }}
        icon={icon}
        tone={tone}
      />
    );
  }

  return (
    <StatCard
      label={label}
      value={tile.count}
      icon={icon}
      tone={tone}
      subLine={
        <Link
          to={tile.filter.path}
          search={query}
          className="text-primary underline-offset-4 hover:underline"
        >
          View
        </Link>
      }
    />
  );
}

function StaleRatesList({
                          items
                        }: {
  items: StaleRateItem[]
}) {
  if (items.length === 0) return <BlockStatus label="No stale rates."/>;

  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead>
        <tr className={TABLE_HEAD_ROW}>
          <th className="px-4 py-2.5 font-semibold">Asset</th>
          <th className="px-4 py-2.5 font-semibold">Network</th>
          <th className="px-4 py-2.5 font-semibold">Last fetched</th>
        </tr>
        </thead>
        <tbody>
        {items.map((item) => (
          <tr key={item.assetId} className="border-t">
            <td className="px-4 py-2 font-medium">{item.symbol}</td>
            <td className="px-4 py-2">
              <StatusBadge label={formatStatusLabel(item.network)} color="neutral" />
            </td>
            <td className="px-4 py-2 text-muted-foreground">
              {item.fetchedAt
                ? new Date(item.fetchedAt).toLocaleString("en-US", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
                : "Never fetched"}
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}

function HealthBlock() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["dashboard", "health"],
    queryFn: getHealthDashboard,
  });

  const hasAnyTile =
    !!data &&
    (data.amlQueueSize !== undefined ||
      data.actionRequiredCount !== undefined ||
      data.failedWebhookDeliveries !== undefined ||
      data.staleRates !== undefined);

  return (
    <BlockCard
      title="Health"
      description="Operational snapshots — current state, not period-bounded."
      icon={Activity}
    >
      {isPending ? (
        <BlockStatus icon={Loader2} spin label="Loading health data…"/>
      ) : isError || !data ? (
        <BlockStatus icon={AlertCircle} label="Failed to load health data."/>
      ) : !hasAnyTile ? (
        <BlockStatus label="No health tiles visible for your role."/>
      ) : (
        <>
          {(data.amlQueueSize || data.actionRequiredCount || data.failedWebhookDeliveries) && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {data.amlQueueSize && (
                <HealthCountStatCard
                  label="AML queue"
                  tile={data.amlQueueSize}
                  icon={ShieldAlert}
                  tone="warning"
                />
              )}
              {data.actionRequiredCount && (
                <HealthCountStatCard
                  label="Action required"
                  tile={data.actionRequiredCount}
                  icon={AlertTriangle}
                  tone="danger"
                />
              )}
              {data.failedWebhookDeliveries && (
                <HealthCountStatCard
                  label="Failed webhook deliveries"
                  tile={data.failedWebhookDeliveries}
                  icon={XCircle}
                  tone="danger"
                />
              )}
            </div>
          )}
          {data.staleRates && (
            <div className="space-y-2">
              <SectionLabel>Stale rates</SectionLabel>
              <StaleRatesList items={data.staleRates}/>
            </div>
          )}
        </>
      )}
    </BlockCard>
  );
}

// ─── merchants block (MERCHANTS_VIEWER) ─────────────────────────────────────

function RankedList({
                      title,
                      rows,
                    }: {
  title: string;
  rows: Array<{ key: string; rank: number; label: string; value: string }>;
}) {
  return (
    <div className="space-y-2">
      <SectionLabel>{title}</SectionLabel>
      {rows.length === 0 ? (
        <BlockStatus label="No data for this period."/>
      ) : (
        <ol className="divide-y rounded-lg border">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="flex min-w-0 items-center gap-3">
                <span className="w-5 shrink-0 text-muted-foreground">{row.rank}</span>
                <span className="truncate font-medium">{row.label}</span>
              </span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{row.value}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function MerchantsBlock({
                          roles, period
                        }: {
  roles: AdminRole[];
  period: DashboardPeriod
}) {
  const canView = hasRole(roles, AdminRole.MERCHANTS_VIEWER);

  const { data, isPending, isError } = useQuery({
    queryKey: ["dashboard", "merchants", period],
    queryFn: () => getMerchantsDashboard(period),
    enabled: canView,
  });

  if (!canView) return null;

  return (
    <BlockCard
      title="Merchants"
      description="Top merchants by volume and fees, plus onboarding activity."
      icon={Store}
    >
      {isPending ? (
        <BlockStatus icon={Loader2} spin label="Loading merchants data…"/>
      ) : isError || !data ? (
        <BlockStatus icon={AlertCircle} label="Failed to load merchants data."/>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <StatCard
              label="Newly onboarded"
              value={data.newlyOnboardedCount}
              icon={UserPlus}
              tone="success"
            />
            <StatCard label="Suspended" value={data.suspendedCount} icon={UserX} tone="danger"/>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <RankedList
              title="Top by volume"
              rows={data.topByVolume.map((m, i) => ({
                key: m.merchantId,
                rank: i + 1,
                label: m.businessName,
                value: formatUsdAmount(m.volumeUsd),
              }))}
            />
            <RankedList
              title="Top by fees"
              rows={data.topByFees.map((m, i) => ({
                key: m.merchantId,
                rank: i + 1,
                label: m.businessName,
                value: formatUsdAmount(m.feesUsd),
              }))}
            />
          </div>
        </>
      )}
    </BlockCard>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

function DashboardPage() {
  const { admin } = Route.useRouteContext();
  const [period, setPeriod] = useState<DashboardPeriod>(DashboardPeriod.H24);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Payments, finance, and merchants tiles reflect the selected period. Health is always current.
        </p>
        <PeriodSwitcher value={period} onChange={setPeriod}/>
      </div>

      <PaymentsBlock roles={admin.roles} period={period}/>
      <FinanceBlock roles={admin.roles} period={period}/>
      <HealthBlock/>
      <MerchantsBlock roles={admin.roles} period={period}/>
    </div>
  );
}
